import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AWAKENING, GROUPS, GROUPS_MAP, ROLES, ROLE_MAP, SKILLS, TAG } from '../config';

import { usePerservedState } from '../utils/versionDataUtil';
import Skill from '../components/Skill';
import './pages.css';

const skillKeys = ["hidden", "special", "skill1", "skill2"];

function CharacterPage({ character, buffData }) {
    const [allAwakening, setAllAwakening] = usePerservedState('savedAwakening', {}, localStorage.getItem('saveAwakening') === 'true');
    const defaultAwakening = localStorage.getItem('defaultAwakening') === 'false';
    const [awakeningStates, setAwakeningStates] = useState(() => {
        return allAwakening && allAwakening[character.id]
            ? {
                h: allAwakening[character.id].h || { awakened: defaultAwakening, variation: 0 },
                "1": allAwakening[character.id]["1"] || { awakened: defaultAwakening, variation: 0 },
                "2": allAwakening[character.id]["2"] || { awakened: defaultAwakening, variation: 0 },
                s: allAwakening[character.id].s || { awakened: defaultAwakening, variation: 0 },
                n: allAwakening[character.id].n || { awakened: defaultAwakening, variation: 0 },
                c: allAwakening[character.id].c || { awakened: defaultAwakening, variation: 0 },
                t: allAwakening[character.id].t || { isTag: character.groups.includes("Tag"), variation: 0 }
            }
            : {
                h: { awakened: defaultAwakening, variation: 0 },
                "1": { awakened: defaultAwakening, variation: 0 },
                "2": { awakened: defaultAwakening, variation: 0 },
                s: { awakened: defaultAwakening, variation: 0 },
                n: { awakened: character.normal?.ax && defaultAwakening ? true : false, variation: 0 },
                c: { awakened: false, variation: 0 },
                t: { isTag: character.groups.includes("Tag"), variation: 0 }
            };
    });

    const hasVariations = skillKeys.some(key => Array.isArray(character[key].a0));

    const skillSections = [
        { skill: character.hidden, title: 'Hidden Technique', type: 'h' },
        { skill: character.skill1, title: 'Skill 1', type: '1' },
        { skill: character.skill2, title: 'Skill 2', type: '2' },
        { skill: character.special, title: 'Special Ability', type: 's' },
        { skill: character.normal, title: 'Normal Attack', type: 'n' },
        { skill: character.combo, title: 'Combo', type: 'c' },
    ];

    const gearSkills = [
        { skill: character.gear1, title: `Gear 1: ${character.gear1.name}`, image: character.gear1.image },
        character.gear2 && { skill: character.gear2, title: `Gear 2: ${character.gear2.name}`, image: character.gear2.image }
    ].filter(Boolean);

    const toggleAwakening = (skill) => {
        setAwakeningStates((prevStates) => {
            const updatedSkillState = { ...prevStates[skill], awakened: !prevStates[skill].awakened };
            const updatedState = { ...prevStates, [skill]: updatedSkillState };

            setAllAwakening(prevAllAwakening => ({
                ...prevAllAwakening,
                [character.id]: updatedState
            }));

            return updatedState;
        });
    };

    const toggleAwakeningVariation = (skill) => {
        setAwakeningStates((prevStates) => {
            const updatedSkillState = {
                ...prevStates[skill],
                variation: prevStates[skill].variation === 0 ? 1 : 0,
            };

            const updatedState = { ...prevStates, [skill]: updatedSkillState };

            setAllAwakening(prevAllAwakening => ({
                ...prevAllAwakening,
                [character.id]: updatedState
            }));

            return updatedState;
        });

    };

    const toggleTag = () => {
        setAwakeningStates((prevStates) => {
            const updatedT = { ...prevStates.t, variation: prevStates.t.variation === 0 ? 1 : 0 };
            const newState = { ...prevStates, t: updatedT };

            setAllAwakening(prevAllAwakening => ({
                ...prevAllAwakening,
                [character.id]: newState
            }));

            return newState;
        });

        toggleAwakeningVariation("t");

        setAwakeningStates((prevStates) => {
            const newState = {};
            for (const skill in prevStates) {
                newState[skill] = { ...prevStates[skill], awakened: prevStates[skill].awakened, variation: awakeningStates.t.variation === 0 ? 1 : 0 };
            }

            setAllAwakening(prevAllAwakening => ({
                ...prevAllAwakening,
                [character.id]: newState
            }));
            return newState;
        });
    };

    const renderSkillSection = (skillData, skillTitle, skillKey) => {
        const isArray = Array.isArray(skillData.a0);
        const currentVariation = awakeningStates[skillKey].variation;
        const isAwakened = awakeningStates[skillKey].awakened;
        const titleSuffix = isAwakened ? ' +' : '';
        const isNcSkill = ['n', 'c'].includes(skillKey);

        const canAwaken = (skillKey, skillData) => {
            return (skillKey !== 'n' && skillData.ax) || (skillKey === 'n' && skillData.ax && character.order.includes('n'));
        };

        let name = skillData.name;
        if (skillKey === 's') {
            name = character.title;
        } else if (Array.isArray(name) && skillKey !== 'n') {
            const variation = awakeningStates.t.isTag ? awakeningStates.t.variation : awakeningStates[skillKey].variation;
            name = skillData.name[variation];
        }

        let image = SKILLS[`${skillData.icon}${isAwakened ? '_a' : ''}`];
        if (skillKey === 'n') {
            image = SKILLS[`1${isAwakened ? '_a' : ''}`]
        } else if (skillKey === 'c') {
            image = SKILLS[character.role]
        } else if (awakeningStates.t.isTag) {
            image = SKILLS[`${skillData.icon[awakeningStates.t.variation]}${isAwakened ? '_a' : ''}`];
        }

        return (
            <div className="skill-section" key={skillKey}>
                <div className="skill-title">
                    <div
                        onClick={() => canAwaken(skillKey, skillData) ? toggleAwakening(skillKey) : null}
                        className={`skill-image-container ${canAwaken(skillKey, skillData) ? 'hover' : ''}`}
                    >
                        <img src={image} alt={name} className="gear-image" />
                        {canAwaken(skillKey, skillData) && (
                            <img
                                className={`awakening-image-medium ${isAwakened ? 'active' : ''}`}
                                src={AWAKENING[`a${character.order.indexOf(skillKey) + 1}`]}
                                alt={`${skillKey} Awakening`}
                            />
                        )}
                    </div>
                    <h3>
                        {isNcSkill ? `${skillTitle}${name || ''}${titleSuffix}` : `${skillTitle}: ${name || ''} ${titleSuffix}`}
                    </h3>
                </div>
                <div className="gear-container">
                    <div className="gear-item">
                        <Skill
                            skill={
                                isArray
                                    ? isAwakened
                                        ? skillData.ax_skills[currentVariation]
                                        : skillData.a0_skills[currentVariation]
                                    : isAwakened
                                        ? skillData.ax_skills
                                        : skillData.a0_skills
                            }
                            buffData={buffData}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderSimpleSkill = (skillData, skillTitle, image) => {
        return (
            <div className="skill-section" key={skillTitle}>
                <div className="skill-title">
                    <div className="skill-image-container">
                        <img
                            src={image}
                            alt={skillTitle}
                            className="gear-image"
                        />
                        {character.gear2?.["ss+"] && skillTitle === `Gear 2: ${character.gear2.name}` && (
                            <>
                                <img
                                    className={`ssplus-image`}
                                    src={AWAKENING["ss+"]}
                                    alt=""
                                />
                            </>
                        )}
                    </div>
                    <h3>{skillTitle}</h3>
                </div>
                <div className="gear-container">
                    <div className="gear-item">
                        <Skill
                            skill={Array.isArray(skillData) ? skillData[awakeningStates.t.variation].a0_skills : skillData.a0_skills}
                            buffData={buffData}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (Object.keys(buffData).length === 0) {
        return (
            <div className="page">
                <div className="main-container">
                    <div className="surround-margin character"></div>
                    <div className="sub-container-character loading">
                        <div className="character-title loading">
                            <div className="image-container">
                                <div className="loading-image" />
                            </div>
                            <div className='title-role loading'>
                                <div className="loading-image long" />
                            </div>
                        </div>
                        <div className="character-page-subheader-container">
                            <h2 className="character-page-subheader">
                                <FormattedMessage
                                    id="main.skills"
                                    defaultMessage="Skills"
                                />
                            </h2>
                        </div>
                        <div className="main-skill-container loading">
                            {Array.from({ length: 7 }).map((_, index) => (
                                <div key={index}>
                                    <div className="skill-title">
                                        <div className="skill-image-container">
                                            <div className="loading-image small" />
                                        </div>
                                    </div>
                                    <div className="gear-item">
                                        <div className="loading-image long" />
                                    </div>
                                    <br />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="surround-margin character"></div>

                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="main-container">
                <div className="surround-margin character"></div>
                <div className="sub-container-character">
                    <div className="character-title">
                        {character.images?.m && (
                            <img
                                className="character-page-image"
                                src={character.images.m}
                                alt={character.name}
                            />
                        )}
                        <div>
                            <div className='title-role'>
                                <div className="character-name-title">
                                    <p>{`${character.name}: ${character.title}`}</p>
                                </div>
                                <img
                                    className="character-page-role"
                                    alt="Role"
                                    src={ROLES[`${ROLE_MAP[character.role]}_${character.colour}`]}
                                />
                            </div>
                            <div className="page-groups">
                                {character.groups.map(group => {
                                    const key = Object.keys(GROUPS_MAP).find(key => GROUPS_MAP[key].includes(group)).toLowerCase().replace(" ", "_");
                                    return (
                                        <div key={key} className="group-title">
                                            <img
                                                className="character-group-image"
                                                src={GROUPS[key]}
                                                alt={key}
                                            />
                                            <p key={group}>
                                                {group}
                                            </p>
                                        </div>
                                    );
                                })}
                                <div className="group-title">
                                    <img
                                        className="character-group-image"
                                        src={GROUPS.position}
                                        alt="Position"
                                    />
                                    <p>
                                        {character.position}
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div className="character-page-subheader-container">
                        <h2 className="character-page-subheader">
                            <FormattedMessage
                                id="main.skills"
                                defaultMessage="Skills"
                            />
                        </h2>
                        {awakeningStates.t.isTag || hasVariations ? (
                            <div onClick={() => toggleTag()} className="tag-container">
                                <img
                                    className="tag-icon"
                                    src={
                                        awakeningStates.t.isTag
                                            ? (awakeningStates.t.variation ? TAG.tag_r : TAG.tag_l)
                                            : (hasVariations && (awakeningStates.t.variation ? TAG.tag_2 : TAG.tag_1))
                                    }
                                    alt=""
                                />
                            </div>
                        ) : null}
                    </div>

                    <div className="main-skill-container">
                        {skillSections.map(section => (
                            renderSkillSection(section.skill, section.title, section.type)
                        ))}
                        {gearSkills.map(gear => (
                            renderSimpleSkill(gear.skill, gear.title, gear.image)
                        ))}
                    </div>
                </div>
                <div className="surround-margin character"></div>

            </div>
        </div>
    );
}

export default CharacterPage;